import {
  Box,
  Checkbox,
  Grid,
  Link,
  Panel,
  PanelSection,
  Paragraph,
  Radio,
  Text,
  TextLabel,
} from '@customerio/pluma-components/react';
import { useMemo } from 'react';
import { EventSelectionFieldset, IntegrationStateToggle, SendFrequencyRadios } from '@/features/integrations';
import { isSegmentOutEnableLocked, type SegmentOutSettingsInput } from '@/services/db/segment-out-integration/entity';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { getDataOutEventGroups } from '@/utils/data-out-events';

const REGIONS_DOC_URL = 'https://segment.com/docs/guides/regional-segment/#regional-data-ingestion';

/**
 * The settings the config form edits. The write key is not among them.
 *
 * Taken from the update body the spec declares, so a field added there is a type
 * error here rather than a silently short PUT.
 */
export type SegmentOutSettings = SegmentOutSettingsInput;

type SettingsFormProps = {
  settings: SegmentOutSettings;
  isReadOnly: boolean;
  onChange: (settings: SegmentOutSettings) => void;
  /** Fires when the reader flips the toggle, with the state it moved to. */
  onEnabledChange: (enabled: boolean) => void;
};

/** Which events to forward, whether forwarding is on, and how Segment receives them. */
export function SettingsForm({ settings, isReadOnly, onChange, onEnabledChange }: SettingsFormProps) {
  const productTaxonomy = useProductTaxonomy();
  const groups = useMemo(() => [...getDataOutEventGroups({ productTaxonomy })], [productTaxonomy]);

  const isEnableLocked = isSegmentOutEnableLocked(settings);

  return (
    <>
      <IntegrationStateToggle
        isEnabled={settings.enabled}
        isReadOnly={isReadOnly}
        isEnableLocked={isEnableLocked}
        onChange={(enabled) => {
          onChange({ ...settings, enabled });
          onEnabledChange(enabled);
        }}
      />

      <PanelSection>
        <Grid columns="minmax(0, 1fr) auto" gap="150" alignItems="flex-start">
          <EventSelectionFieldset
            groups={groups}
            selectedItems={settings.events}
            isDisabled={isReadOnly}
            onChange={(events) => onChange({ ...settings, events })}
          />

          <Box as="fieldset">
            <Text text="product-h4" as="legend" mb="150">
              Options
            </Text>
            <Panel backgroundColor="subtle">
              <PanelSection>
                <Box as="fieldset" mb="100">
                  <TextLabel as="legend" mb="150">
                    Send frequency
                  </TextLabel>
                  <SendFrequencyRadios
                    resolution={settings.resolution}
                    isDisabled={isReadOnly}
                    onChange={(resolution) => onChange({ ...settings, resolution })}
                  />
                </Box>

                {/*
                  Ember left these two ungated while every other control on the form
                  checks the edit permission, which let a read-only reader change
                  them. Gated with the rest.
                */}
                <Box as="fieldset" mb="150">
                  <TextLabel as="legend" mb="150">
                    Event content
                  </TextLabel>
                  <Checkbox
                    name="include_body_content"
                    label="Include body content in all events sent to Segment"
                    isChecked={settings.with_content}
                    isDisabled={isReadOnly}
                    onCheckedChange={(checked) => onChange({ ...settings, with_content: checked })}
                  />
                  <Checkbox
                    name="send_cio"
                    label="Send Segment events back to Customer.io destinations"
                    isChecked={settings.send_cio}
                    isDisabled={isReadOnly}
                    onCheckedChange={(checked) => onChange({ ...settings, send_cio: checked })}
                  />
                </Box>

                <Box as="fieldset">
                  <TextLabel as="legend" mb="150">
                    Segment data ingestion region
                  </TextLabel>
                  <Radio
                    id="segment_region_us_west"
                    name="segment_region"
                    label="US West (default)"
                    isChecked={!settings.eu}
                    isDisabled={isReadOnly}
                    onCheckedChange={() => onChange({ ...settings, eu: false })}
                  />
                  <Radio
                    id="segment_region_eu_west"
                    name="segment_region"
                    label="EU West"
                    isChecked={settings.eu}
                    isDisabled={isReadOnly}
                    onCheckedChange={() => onChange({ ...settings, eu: true })}
                  />
                  <Paragraph mb="200" color="subtle" size="xs">
                    Learn more about{' '}
                    <Link href={REGIONS_DOC_URL} variant="secondary" isExternal>
                      Segment's data ingestion regions.
                    </Link>
                  </Paragraph>
                </Box>
              </PanelSection>
            </Panel>
          </Box>
        </Grid>
      </PanelSection>
    </>
  );
}
